import { BlurHashImage } from 'shared/components/connected/blur-hash-image';
import { typographyToCss } from 'shared/lib/mui';
import { QueryNavLink } from 'shared/lib/react-router-dom';
import styled, { css } from 'styled-components';

export const Container = styled.div<{
  $isGrabbable: boolean;
  $isGrabbed: boolean;
}>`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};

  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(1.5)}`};
  width: 100%;
  min-width: 0;

  border-radius: ${({ theme }) => theme.spacing(2)};
  cursor: ${({ $isGrabbed }) => ($isGrabbed ? 'grabbing' : 'default')};

  color: ${({ theme }) => theme.palette.grey[400]};

  ${({ theme, $isGrabbable }) =>
    $isGrabbable &&
    css`
      &:hover {
        background: ${theme.palette.grey[50]};
      }
    `};
`;

export const Content = styled.div<{ $completed: boolean; $archived: boolean }>`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1.5)};

  min-width: 0;

  color: ${({ theme, $completed, $archived }) =>
    $completed || $archived
      ? theme.palette.text.secondary
      : theme.palette.text.primary};
`;

export const IllustrationContainer = styled.span<{
  $color: string | null | undefined;
}>`
  position: relative;
  width: 2rem;
  min-width: 2rem;
  height: 2rem;
  min-height: 2rem;

  border-radius: ${({ theme }) => theme.spacing(1.5)};

  color: ${({ theme }) => theme.palette.grey[400]};
  font-size: 1.2rem;
  line-height: 62.5%;

  ${({ $color }) =>
    $color &&
    css`
      &::before {
        content: '';
        position: absolute;
        bottom: 0;
        right: 0;

        width: 0.6rem;
        height: 0.6rem;

        background: ${$color};
        border-radius: 50%;
        border: 1px solid ${({ theme }) => theme.palette.common.white};
        transform: translate(33%, 33%);
      }
    `}
`;

export const ImageContainer = styled.span`
  display: block;
  width: 100%;
  height: 100%;

  border-radius: ${({ theme }) => theme.spacing(1.5)};
  overflow: hidden;
`;

export const Image = styled(BlurHashImage)`
  display: block;
  width: 100%;
  height: 100%;

  object-fit: cover;
`;

export const ProgressContainer = styled.span<{ $color: string | null }>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;

  color: ${({ $color }) => $color ?? 'inherit'};
  font-size: 1.6rem;
  line-height: 62.5%;
`;

export const IconContainer = styled.span<{
  $noBackground: boolean;
  $error: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 2rem;
  min-width: 2rem;
  height: 2rem;
  min-height: 2rem;

  border-radius: ${({ theme }) => theme.spacing(1.5)};
  background: ${({ theme, $noBackground }) =>
    $noBackground ? 'transparent' : theme.palette.grey[100]};
  overflow: hidden;

  line-height: 62.5%;
  color: ${({ theme, $error }) =>
    $error ? theme.palette.error.main : theme.palette.grey[700]};

  ${({ $noBackground }) =>
    $noBackground &&
    css`
      font-size: 1.6rem;
    `};
`;

export const CompletedContainer = styled.span`
  position: absolute;
  top: 0;
  right: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 1.4rem;
  height: 1.4rem;

  transform: translate(22.5%, -22.5%);
  background: ${({ theme }) => theme.palette.success.main};
  border-radius: 50%;

  font-size: 0.8rem;
  color: ${({ theme }) => theme.palette.success.contrastText};
`;

export const ChevronContainer = styled.span`
  opacity: 0;
  transform: rotate(180deg);
  transition: opacity 200ms;

  line-height: 62.5%;
  font-size: 1.6rem;
  color: ${({ theme }) => theme.palette.grey[600]};
`;

export const TitleContainer = styled(QueryNavLink)<{ $completed: boolean }>`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing(0.25)} 0`};

  min-width: 0;

  text-decoration: ${({ $completed }) =>
    $completed ? 'line-through' : 'none'};
  color: inherit;

  &:hover {
    ${ChevronContainer} {
      opacity: 1;
    }
  }
`;

export const Title = styled.span<{ $isH6: boolean }>`
  ${({ theme, $isH6 }) =>
    typographyToCss($isH6 ? theme.typography.h6 : theme.typography.subtitle2)};

  overflow: hidden;

  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Badge = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.subtitle2)};

  padding: ${({ theme }) => `0 0 0 ${theme.spacing(1)}`};

  color: ${({ theme }) => theme.palette.grey[600]};
`;

export const MetaContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const DeadlineContainer = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.caption)};
  display: inline-block;
  padding: ${({ theme }) => `0 ${theme.spacing(1)}`};

  border-radius: ${({ theme }) => theme.spacing(1)};
  background: ${({ theme }) => theme.palette.grey[50]};
  color: ${({ theme }) => theme.palette.grey[700]};
`;

export const NoDatesContainer = styled.div`
  color: ${({ theme }) => theme.palette.text.secondary};
  white-space: nowrap;
  line-height: 62.5%;
`;
